import { useCallback, useEffect, useMemo } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useHwcgFlag } from 'utils/featureFlags';
import { ReviewQueueDialog } from 'components/ReviewQueue/ReviewQueueDialog';
import { useReviewQueue } from 'components/ReviewQueue/useReviewQueue';
import {
  REVIEW_QUEUE_KIND_LABELS,
  parseReviewQueueOpen,
  reviewQueueOpenKey,
  type ReviewQueueItem,
} from 'components/ReviewQueue/reviewQueueTypes';

function formatWhen(value: string | null) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

function ReviewQueueTable({
  items,
  showIncident,
  onOpen,
}: {
  items: ReviewQueueItem[];
  showIncident: boolean;
  onOpen: (item: ReviewQueueItem) => void;
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Type</TableHead>
          <TableHead>Name</TableHead>
          {showIncident ? <TableHead>Incident</TableHead> : null}
          <TableHead>Waiting on</TableHead>
          <TableHead>Submitted by</TableHead>
          <TableHead>Submitted</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((item) => (
          <TableRow
            key={reviewQueueOpenKey(item)}
            className="tw:cursor-pointer"
            tabIndex={0}
            onClick={() => onOpen(item)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onOpen(item);
              }
            }}
          >
            <TableCell>{REVIEW_QUEUE_KIND_LABELS[item.kind]}</TableCell>
            <TableCell>{item.title}</TableCell>
            {showIncident ? (
              <TableCell>{item.incidentName || '—'}</TableCell>
            ) : null}
            <TableCell>{item.waitingOn}</TableCell>
            <TableCell>{item.submittedBy || '—'}</TableCell>
            <TableCell>{formatWhen(item.submittedAt)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/**
 * HWCG review queue. On the hub, incident-scoped rows open inside that
 * incident so the existing review hooks see the right workspace.
 */
export function ReviewQueuePage({
  incidentId,
}: {
  incidentId: string | null;
}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queue = useReviewQueue(incidentId);
  const open = parseReviewQueueOpen(searchParams.get('open'));
  const items = queue.data ?? [];
  const openItem = useMemo(
    () =>
      open
        ? items.find((item) => item.kind === open.kind && item.id === open.id)
        : undefined,
    [items, open]
  );

  useEffect(() => {
    if (!openItem?.incidentId || incidentId) return;
    navigate(
      `/iap/${openItem.incidentId}/review-queue?open=${reviewQueueOpenKey(openItem)}`
    );
  }, [incidentId, navigate, openItem]);

  const clearOpen = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('open');
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  useEffect(() => {
    if (!open || queue.isLoading || queue.isFetching || !queue.data) return;
    const stillWaiting = queue.data.some(
      (item) => item.kind === open.kind && item.id === open.id
    );
    if (!stillWaiting) clearOpen();
  }, [clearOpen, open, queue.data, queue.isFetching, queue.isLoading]);

  const onOpen = (item: ReviewQueueItem) => {
    if (!incidentId && item.incidentId) {
      navigate(
        `/iap/${item.incidentId}/review-queue?open=${reviewQueueOpenKey(item)}`
      );
      return;
    }
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('open', reviewQueueOpenKey(item));
      return next;
    });
  };

  const showDialog = Boolean(openItem && (incidentId || !openItem.incidentId));

  return (
    <div className="tw:flex tw:h-full tw:min-h-0 tw:flex-col tw:gap-4 tw:p-4">
      <div>
        <h1 className="tw:m-0 tw:text-lg tw:font-semibold">Review Queue</h1>
        <p className="tw:m-0 tw:mt-1 tw:text-sm tw:text-muted-foreground">
          Items waiting on you to review.
        </p>
      </div>
      {queue.isLoading ? (
        <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">Loading…</p>
      ) : null}
      {queue.isError ? (
        <p className="tw:m-0 tw:text-sm tw:text-destructive">
          Couldn&apos;t load the review queue.
        </p>
      ) : null}
      {!queue.isLoading && !queue.isError && items.length === 0 ? (
        <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
          Nothing is waiting on you.
        </p>
      ) : null}
      {items.length > 0 ? (
        <ReviewQueueTable
          items={items}
          showIncident={!incidentId}
          onOpen={onOpen}
        />
      ) : null}
      {showDialog && openItem ? (
        <ReviewQueueDialog item={openItem} onClose={clearOpen} />
      ) : null}
    </div>
  );
}

export function IncidentReviewQueuePage() {
  const hwcg = useHwcgFlag();
  const { incidentId } = useParams<{ incidentId: string }>();
  if (!hwcg) {
    return <Navigate to={`/iap/${incidentId}/dashboard`} replace />;
  }
  return <ReviewQueuePage incidentId={incidentId ?? null} />;
}
